import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Check, Target, BarChart3, Zap, Shield, TrendingUp,
  Smartphone, Users, MapPin, Eye, RefreshCw, Store,
  Dumbbell, Scissors, Sparkles, Home as HomeIcon, Heart,
  Dog, ShoppingBag, Wrench, Phone, Building2
} from 'lucide-react';
import Header from '../components/Header';
import ContactFormModal from '../components/ContactFormModal';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const JoinTappd = () => {
  const [email, setEmail] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div style={{ backgroundColor: '#FEF7ED', minHeight: '100vh' }}>
      <SEO 
        title="Join Tappd - Advertise Your Business"
        description="Join Tappd and reach local customers inside bars and restaurants. Place your brand on QR-powered coasters and track every scan, visit, and redemption."
        url="/join-tappd"
        keywords="join Tappd, business advertising, QR coaster advertising, local marketing platform"
      />
      <Header />

      {/* SECTION 1 - Hero */}
      <section className="relative overflow-hidden" style={{ minHeight: '100vh', paddingTop: '8rem' }}>
        {/* Background Elements - Using EMAIL-BG-2 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <img src="https://iolab.co/wp-content/uploads/2025/12/EMAIL-BG-2.png" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-1/4 -right-32 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" style={{ animation: 'pulse 3s ease-in-out infinite' }}></div>
          <div className="absolute bottom-1/4 -left-32 w-80 h-80 bg-orange-400/20 rounded-full blur-3xl" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '0.3s' }}></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-[920px]">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
                className="mb-6 flex justify-center"
              >
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(111, 91, 235, 0.2)',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
                }}>
                  <Sparkles size={24} style={{ color: '#6F5BEB' }} />
                  <span className="font-semibold text-base" style={{ fontFamily: "'DM Sans', sans-serif", color: '#1e1b4b', letterSpacing: '0.05em' }}>
                    ADVERTISE WITH TAPPD
                  </span>
                </div>
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-6" 
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(2.5rem, 8vw, 4rem)',
                  lineHeight: '1.1',
                  letterSpacing: '-0.02em'
                }}
              >
                <span style={{ color: '#1e1b4b' }}>Reach Local Customers</span>
                <br />
                <span style={{ 
                  background: 'linear-gradient(135deg, #6F5BEB 0%, #8B7CF7 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Inside Bars & Restaurants
                </span>
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mb-8" 
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: 'rgba(30, 27, 75, 0.7)',
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.25rem)',
                  lineHeight: '1.6',
                  maxWidth: '720px',
                  margin: '0 auto 32px'
                }}
              >
                Place your brand on <strong style={{ color: '#6F5BEB' }}>QR-powered coasters</strong> across local bars & eateries. Track every scan, visit, and redemption in real-time.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Button 
                  onClick={() => setIsContactModalOpen(true)}
                  className="px-10 py-6 text-xl font-bold rounded-full shadow-2xl"
                  style={{ 
                    background: 'linear-gradient(135deg, #6F5BEB 0%, #8B7CF7 100%)',
                    color: '#FFFFFF',
                    boxShadow: '0 10px 40px rgba(111, 91, 235, 0.3)'
                  }}
                >
                  Talk to Sales
                </Button>
                <Link to="/pricing">
                  <Button 
                    variant="outline"
                    className="px-10 py-6 text-xl font-bold rounded-full shadow-lg w-full"
                    style={{ 
                      borderColor: '#1e1b4b',
                      borderWidth: '2px',
                      color: '#1e1b4b',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)'
                    }}
                  >
                    See Pricing →
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1 }}
                className="mt-12"
              >
                <p 
                  className="text-sm mb-4"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: 'rgba(30, 27, 75, 0.5)',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase'
                  }}
                >
                  Perfect For
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {['Gyms', 'Salons', 'Spas', 'Real Estate', 'Pet Services', 'Retail'].map((type) => (
                    <span
                      key={type}
                      className="px-4 py-2 rounded-full text-sm font-semibold"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        border: '1px solid rgba(111, 91, 235, 0.2)',
                        color: '#1e1b4b',
                        fontFamily: "'DM Sans', sans-serif"
                      }}
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, repeat: Infinity, repeatType: "reverse" }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <motion.div
              className="w-1.5 h-1.5 bg-white rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* SECTION 2 - Slim Gradient Banner */}
      <section 
        className="py-8"
        style={{
          background: 'linear-gradient(135deg, #6F5BEB 0%, #FF7A1A 50%, #F59E0B 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-6">
            <h2 
              className="text-2xl md:text-3xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advertise inside local bars — without having to partner with them individually.
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Smartphone, text: 'No app required' },
              { icon: Shield, text: 'No contracts with venues' },
              { icon: BarChart3, text: 'Live analytics' },
              { icon: Store, text: 'Ready-made bar network' }
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center text-center">
                <item.icon size={32} className="text-white mb-2" />
                <p className="text-white font-semibold" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 - What is Tappd */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
            >
              What is Tappd for local businesses?
            </h2>
            <p 
              className="text-xl max-w-3xl mx-auto"
              style={{ fontFamily: "'DM Sans', sans-serif", color: '#475569' }}
            >
              Tappd lets you run trackable ads inside local bars and restaurants using QR-powered coasters placed under every drink.
            </p>
            <p 
              className="text-2xl font-bold mt-4"
              style={{ fontFamily: "'Playfair Display', serif", color: '#FF7A1A' }}
            >
              Coaster network = your new local billboard.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: MapPin,
                title: 'Hyperlocal Targeting',
                description: 'Ads appear only in venues near your business.'
              },
              {
                icon: Smartphone,
                title: 'QR-Powered Offers',
                description: 'Drive instant action with scannable deals and promos.'
              },
              {
                icon: BarChart3,
                title: 'Real-Time Analytics',
                description: 'Track scans, clicks, and redemptions — instantly.'
              },
              {
                icon: Zap,
                title: 'Turn-Key Distribution',
                description: 'Your brand appears across our existing bar & restaurant partners.'
              },
              {
                icon: RefreshCw,
                title: 'Dynamic Offer Editing',
                description: 'Change your offer anytime without reprinting anything.'
              },
              {
                icon: Store,
                title: 'Perfect for Non-Restaurant Businesses',
                description: 'Gyms, salons, boutiques, real estate agents, pet services, home services, etc.'
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: '#FF7A1A20' }}
                >
                  <feature.icon size={28} style={{ color: '#FF7A1A' }} />
                </div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
                >
                  {feature.title}
                </h3>
                <p 
                  style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                >
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 - Why Businesses Love Tappd */}
      <section 
        className="py-20 px-6"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
              >
                What local businesses love about Tappd
              </h2>
              <p 
                className="text-xl"
                style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
              >
                Join hundreds of businesses already reaching customers in their favorite hangout spots.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjBzb2NpYWxpemluZ3xlbnwwfHx8fDE3NjMyMjU2MTJ8MA&ixlib=rb-4.1.0&q=85"
                alt="People socializing"
                className="w-full h-[300px] object-cover"
              />
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Reach people you can't reach online",
                description: 'Bars + restaurants = high dwell time, phone-in-hand audience.'
              },
              {
                title: 'Perfect for awareness + walk-ins',
                description: 'Your offer reaches people nearby, already in a spending mindset.'
              },
              {
                title: 'Zero venue partnerships required',
                description: 'No negotiating table tent or flyer placement. We handle everything.'
              },
              {
                title: 'Measurable results',
                description: 'Every scan, view, visit, and redemption appears in your dashboard.'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #6F5BEB 0%, #FF7A1A 100%)' }}
                >
                  {idx + 1}
                </div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
                >
                  {item.title}
                </h3>
                <p 
                  style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                >
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 - How Tappd Works */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
            >
              How Tappd works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Create Your Offer',
                description: 'Upload your promo (free week at your gym, salon discount, real estate consultation, etc.).'
              },
              {
                step: '2',
                title: 'We place your ad inside local bars',
                description: 'Your brand enters our coaster rotation across participating venues.'
              },
              {
                step: '3',
                title: 'Customers scan while drinking',
                description: 'They see your offer, learn about your business, and redeem it.'
              },
              {
                step: '4',
                title: 'You track everything',
                description: 'See scans, views, and engagement in real time.'
              }
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg h-full">
                  <div 
                    className="text-6xl font-bold mb-4 opacity-20"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#FF7A1A' }}
                  >
                    {step.step}
                  </div>
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
                  >
                    {step.title}
                  </h3>
                  <p 
                    style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                  >
                    {step.description}
                  </p>
                </div>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <div className="text-4xl text-gray-300">→</div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 - Brand Safety */}
      <section 
        className="py-20 px-6"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src="https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGFuYWx5dGljc3xlbnwwfHx8fDE3NjMyMjU2MDd8MA&ixlib=rb-4.1.0&q=85"
                alt="Business analytics dashboard"
                className="w-full h-[350px] object-cover"
              />
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
              >
                Brand-safe placements + full transparency
              </h2>
              <p 
                className="text-xl"
                style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
              >
                Track every scan, view, and redemption with real-time analytics you can trust.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Curated Venue Network',
                description: 'Your ads appear only in vetted, brand-safe bars and restaurants.'
              },
              {
                icon: BarChart3,
                title: 'Real-Time Metrics',
                description: 'Track scans, redemptions, and top-performing venues.'
              },
              {
                icon: Target,
                title: 'Attribution Ready',
                description: 'Promo codes & UTMs make ROI provable.'
              },
              {
                icon: Eye,
                title: 'Privacy-Friendly',
                description: 'We give you actionable analytics — not creepy tracking.'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div 
                  className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #6F5BEB 0%, #FF7A1A 100%)' }}
                >
                  <item.icon size={36} className="text-white" />
                </div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
                >
                  {item.title}
                </h3>
                <p 
                  style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                >
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 - Who It's For */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
            >
              Perfect for local businesses
            </h2>
            <div className="rounded-2xl overflow-hidden shadow-xl max-w-3xl mx-auto mb-8">
              <img
                src="https://images.unsplash.com/photo-1571204829887-3b8d69e4094d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsb2NhbCUyMGJ1c2luZXNzfGVufDB8fHx8MTc2MzIyNTYwMnww&ixlib=rb-4.1.0&q=85"
                alt="Thank you for shopping local"
                className="w-full h-[300px] object-cover"
              />
            </div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4 mb-12">
              {[
                { icon: Dumbbell, text: 'Gyms & fitness studios' },
                { icon: Scissors, text: 'Hair salons + barbers' },
                { icon: Sparkles, text: 'Spas & wellness' },
                { icon: Building2, text: 'Real estate professionals' },
                { icon: Dog, text: 'Pet groomers, daycares, vets' },
                { icon: ShoppingBag, text: 'Boutiques & local retail' },
                { icon: Wrench, text: 'Home services (cleaning, repair, etc.)' },
                { icon: Heart, text: 'Tattoo studios, massage studios' },
                { icon: Phone, text: 'Insurance & financial agents' },
                { icon: HomeIcon, text: 'Local attractions / entertainment venues' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm"
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#FF7A1A20' }}
                  >
                    <item.icon size={24} style={{ color: '#FF7A1A' }} />
                  </div>
                  <span 
                    className="font-semibold"
                    style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}
                  >
                    {item.text}
                  </span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-8 text-center"
            >
              <p 
                className="text-lg font-semibold"
                style={{ fontFamily: "'DM Sans', sans-serif", color: '#FF7A1A' }}
              >
                🍺 Bars & restaurants: You&apos;re already part of our network. Tappd advertising is for businesses who want to reach your guests.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 8 - Social Proof */}
      <section 
        className="py-20 px-6"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
              >
                Tappd is already connecting local customers with businesses like yours.
              </h2>
              <p 
                className="text-xl"
                style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
              >
                Real results from real local businesses across the country.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src="https://images.unsplash.com/photo-1556741533-6e6a62bd8b49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxsb2NhbCUyMGJ1c2luZXNzfGVufDB8fHx8MTc2MzIyNTYwMnww&ixlib=rb-4.1.0&q=85"
                alt="Local business"
                className="w-full h-[300px] object-cover"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {[
              { number: '1,000+', label: 'coaster scans monthly' },
              { number: '250+', label: 'businesses discovered' },
              { number: '150', label: 'active offers' },
              { number: '30+', label: 'participating bars & restaurants' }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div 
                  className="text-5xl font-bold mb-2"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#FF7A1A' }}
                >
                  {stat.number}
                </div>
                <p 
                  style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                >
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {['FitPoint Gym', 'Bella Hair Studio', 'DogSpa Grooming', 'Thrive Chiropractic', 'Green Thumb Nursery'].map((name, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.1 }}
                className="bg-gray-100 rounded-xl px-8 py-4"
              >
                <p 
                  className="font-bold text-lg"
                  style={{ fontFamily: "'DM Sans', sans-serif", color: '#475569' }}
                >
                  {name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 - Pricing Preview */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
            >
              Simple, scalable plans for any local business
            </h2>
            <p 
              className="text-xl"
              style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
            >
              Start small. Scale up as you see results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {[
              { name: 'Basic', price: '$113', description: 'Test the coaster network', popular: false },
              { name: 'Medium', price: '$188', description: 'Most businesses choose this', popular: true },
              { name: 'High', price: '$300', description: 'Maximum visibility', popular: false }
            ].map((plan, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.1 }}
                className={`bg-white rounded-2xl p-8 shadow-lg ${plan.popular ? 'ring-4 ring-purple-500' : ''} relative`}
              >
                {plan.popular && (
                  <div 
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-full text-sm font-bold text-white"
                    style={{ backgroundColor: '#6F5BEB' }}
                  >
                    Most Popular
                  </div>
                )}
                <h3 
                  className="text-2xl font-bold mb-2"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span 
                    className="text-5xl font-bold"
                    style={{ fontFamily: "'Playfair Display', serif", color: '#FF7A1A' }}
                  >
                    {plan.price}
                  </span>
                  <span 
                    className="text-xl"
                    style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                  >
                    /mo
                  </span>
                </div>
                <p 
                  className="mb-6"
                  style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                >
                  {plan.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link 
              to="/pricing"
              className="text-lg font-semibold inline-flex items-center gap-2 hover:underline"
              style={{ color: '#FF7A1A', fontFamily: "'DM Sans', sans-serif" }}
            >
              View full pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 10 - FAQ */}
      <section 
        className="py-20 px-6"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold text-center mb-16"
            style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="space-y-6">
            {[
              {
                question: 'Do I need to partner with bars myself?',
                answer: 'No! We handle all venue relationships. You just create your offer and we distribute it across our network.'
              },
              {
                question: 'How many people will see my offer?',
                answer: 'It depends on your plan tier and local foot traffic. Most businesses see hundreds to thousands of impressions per month.'
              },
              {
                question: 'Can I run multiple promotions?',
                answer: 'Yes! You can update your offer as often as you like through your dashboard.'
              },
              {
                question: 'Can I promote a service, not a product?',
                answer: 'Absolutely! Tappd works great for service-based businesses like gyms, salons, real estate agents, and more.'
              },
              {
                question: "Does Tappd work if my business isn't nearby?",
                answer: 'Tappd works best for businesses within 1-2 miles of our venue network. We focus on hyperlocal targeting.'
              },
              {
                question: 'What industries work best?',
                answer: 'Any local business except bars and restaurants. Top performers include fitness, beauty, wellness, pet services, and home services.'
              },
              {
                question: 'Is there a contract?',
                answer: 'No long-term contracts. You can cancel anytime with 30 days notice.'
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: idx * 0.05 }}
                className="bg-gray-50 rounded-xl p-6"
              >
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{ fontFamily: "'Playfair Display', serif", color: '#111827' }}
                >
                  {faq.question}
                </h3>
                <p 
                  style={{ fontFamily: "'DM Sans', sans-serif", color: '#64748b' }}
                >
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11 - Final CTA */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'linear-gradient(135deg, #FFD700 0%, #FF7A1A 50%, #6F5BEB 100%)'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 
              className="text-4xl md:text-6xl font-bold mb-6 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ready to put your business inside local bars and restaurants?
            </h2>
            
            <Button 
              onClick={() => setIsContactModalOpen(true)}
              className="px-12 py-8 text-xl font-bold rounded-2xl mb-6"
              style={{ 
                backgroundColor: '#FFFFFF',
                color: '#FF7A1A'
              }}
            >
              Talk to Sales
            </Button>

            <p 
              className="text-lg text-white"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              We'll recommend the best plan in 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer Gradient */}
      <div 
        style={{
          height: '8px',
          background: 'linear-gradient(to right, #6F5BEB 0%, #FF7A1A 50%, #F59E0B 100%)'
        }}
      />

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
};

export default JoinTappd;
